var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LoginAnalyticsEvent } from "healthex-common/analytics.dtos";
import { AuthTarget, authTargetFromTefcaProvider } from "healthex-common/clear.dtos";
import { FeatureFlag, TefcaProvider } from "healthex-common/features.dtos";
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
import { LoginSource } from "healthex-common/heap.dtos";
import { isOk } from "healthex-common/result";
import { computed, observable, reaction } from "mobx";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
import { ClearAuthState, ClearFallbackData, Persistence } from "../Infra/Persistence";
import { getAnonymousUserId } from "../Logic/AuthManager";
import { NamedRoutes } from "./NamedRoutes";
import { effectiveQueryParams } from "./queryParams";
export var ClearInitialScreen;
(function (ClearInitialScreen) {
    ClearInitialScreen["OTP_VERIFY_PASSCODE"] = "OTP_VERIFY_PASSCODE";
    ClearInitialScreen["OTP_LOCKOUT"] = "OTP_LOCKOUT";
    ClearInitialScreen["PATIENT_CONSENT_CLEAR_UPGRADE"] = "PATIENT_CONSENT_CLEAR_UPGRADE";
})(ClearInitialScreen || (ClearInitialScreen = {}));
export class ClearAuthManager {
    constructor(authManager, persistence, routingUIManager, analyticsService, clearService, featureManager, patientService) {
        this.authManager = authManager;
        this.persistence = persistence;
        this.routingUIManager = routingUIManager;
        this.analyticsService = analyticsService;
        this.clearService = clearService;
        this.featureManager = featureManager;
        this.patientService = patientService;
        this.state = ClearAuthState.INACTIVE;
        reaction(() => this.routingUIManager.currentRoute, (currentRoute) => {
            if ((currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.routeName) === NamedRoutes.CLEAR_IAL2_CALLBACK.routeName) {
                void this.processClearAuthCallback();
            }
        }, { fireImmediately: true, name: "ClearAuthManager_currentRoute" });
        window.addEventListener("pageshow", (event) => {
            if (event.persisted) {
                this.resetAuthStateIfNeeded();
            }
        });
    }
    get hasCompletedAuth() {
        return this.state === ClearAuthState.SUCCESS || this.state === ClearAuthState.ERROR || this.state === ClearAuthState.EXITED;
    }
    async redirectToClearLoginPage(callbackHash, source, organizationId, useFallback, sandboxMode, emailAddress, language, nextPageRoute, initialScreen, variant) {
        this.state = ClearAuthState.IN_PROGRESS;
        const resolvedOrganizationId = typeof organizationId === "function" ? await organizationId() : organizationId;
        const actuallyUseFallback = await this.determineUseFallback(useFallback !== null && useFallback !== void 0 ? useFallback : false);
        this.persistence.setClearFallbackData(actuallyUseFallback ? new ClearFallbackData({ fallbackLastUsedAt: new Date() }) : null);
        console.log(`Using IAL2 verification: ${!actuallyUseFallback}`);
        let authTarget;
        const trimmedEmail = (emailAddress === null || emailAddress === void 0 ? void 0 : emailAddress.trim()) || null;
        if (actuallyUseFallback) {
            authTarget = AuthTarget.Fallback;
        }
        else if (trimmedEmail) {
            const provider = await this.patientService.getPatientTefcaProvider(trimmedEmail, resolvedOrganizationId);
            if (provider === TefcaProvider.HxTest)
                throw new Error("HxTest patients are not supported for CLEAR login");
            authTarget = authTargetFromTefcaProvider(provider);
        }
        else {
            const provider = await this.patientService.getDefaultTefcaProvider();
            if (provider === TefcaProvider.HxTest)
                throw new Error("HxTest patients are not supported for CLEAR login");
            authTarget = authTargetFromTefcaProvider(provider);
        }
        console.log(`Launching CLEAR login with authTarget ${authTarget}`);
        const heapGlobalEventProperties = {
            ...getHeapGlobalEventProperties(),
            source,
            provider: GFIDProvider.CLEAR,
            ...(variant !== undefined ? { variant } : {}),
        };
        const { state, url } = await this.clearService.getIAL2Url(this.getRedirectUri(), authTarget, sandboxMode, heapGlobalEventProperties);
        const urlWithLanguage = language ? `${url}${url.includes("?") ? "&" : "?"}cv_locale=${language}` : url;
        this.setClearAuthData({
            callbackHash,
            state,
            sandboxMode,
            heapGlobalEventProperties,
            authTarget,
            organizationId: resolvedOrganizationId !== null && resolvedOrganizationId !== void 0 ? resolvedOrganizationId : undefined,
            source,
            nextPageRoute,
            initialScreen,
            collectedEmail: trimmedEmail !== null && trimmedEmail !== void 0 ? trimmedEmail : undefined,
        });
        window.location.href = urlWithLanguage;
    }
    static checkAndReturnClearAuthResponse() {
        const urlParams = effectiveQueryParams();
        const error = urlParams.get("error");
        if (error === "cancelled") {
            console.log("checkAndReturnClearAuthResponse: cancelled");
            return { error };
        }
        const clearAuthData = new Persistence().getClearAuthData();
        if (!clearAuthData) {
            console.log("checkAndReturnClearAuthResponse: no clear auth data");
            return null;
        }
        if (error) {
            console.log("checkAndReturnClearAuthResponse: error", error);
            return { error };
        }
        const code = urlParams.get("code");
        const scope = urlParams.get("scope");
        const state = urlParams.get("state");
        if (code && scope && state) {
            console.log("checkAndReturnClearAuthResponse: code, scope, state are present");
            return { code, scope, state };
        }
        console.log("checkAndReturnClearAuthResponse: no code, scope, state");
        return null;
    }
    static clearIal2CallbackRedirectUrl() {
        const clearAuthResult = ClearAuthManager.checkAndReturnClearAuthResponse();
        const alreadyOnClearCallback = window.location.hash.startsWith("#/clear-ial2-callback");
        if (clearAuthResult === null || alreadyOnClearCallback) {
            return null;
        }
        const query = window.location.search || (window.location.hash.includes("?") ? `?${window.location.hash.split("?")[1]}` : "");
        return `${window.location.origin}${window.location.pathname}#/clear-ial2-callback${query}`;
    }
    static applyIal2CallbackRedirect() {
        console.log("applyIal2CallbackRedirect check");
        const redirectUrl = ClearAuthManager.clearIal2CallbackRedirectUrl();
        if (!redirectUrl)
            return;
        window.history.replaceState(null, "", redirectUrl);
    }
    resetAuth() {
        console.log("Resetting CLEAR auth state");
        this.state = ClearAuthState.INACTIVE;
        this.persistence.setClearAuthData(null);
    }
    setClearAuthData(clearAuthData) {
        if (clearAuthData === null) {
            console.log("Setting CLEAR auth data to null");
        }
        else {
            console.log(`Setting CLEAR auth data to: ${JSON.stringify(clearAuthData)}`);
        }
        this.persistence.setClearAuthData(clearAuthData);
    }
    getClearAuthData() {
        return this.persistence.getClearAuthData();
    }
    async determineUseFallback(useFallback) {
        const rolloutEnabledFeature = this.featureManager.isFeatureEnabled(FeatureFlag.RO_CLEAR_LOGIN_FALLBACK);
        if (!rolloutEnabledFeature) {
            return false;
        }
        if (useFallback) {
            return true;
        }
        const clearFallbackData = this.persistence.getClearFallbackData();
        if (!clearFallbackData) {
            return false;
        }
        const resetFeature = this.featureManager.isFeatureEnabled(FeatureFlag.CLEAR_LOGIN_FALLBACK_RESET);
        if (resetFeature) {
            const resetDate = this.featureManager.decodeFeatureConfiguration(FeatureFlag.CLEAR_LOGIN_FALLBACK_RESET);
            if (resetDate instanceof Date && resetDate > clearFallbackData.fallbackLastUsedAt) {
                this.persistence.setClearFallbackData(null);
                console.log(`Reset to IAL2 verification`);
                return false;
            }
        }
        return true;
    }
    async processClearAuthCallback() {
        var _a, _b, _c;
        const clearAuthData = this.getClearAuthData();
        const clearAuthResponse = ClearAuthManager.checkAndReturnClearAuthResponse();
        if (!clearAuthResponse) {
            this.handleAuthError();
            return;
        }
        if ("error" in clearAuthResponse) {
            if (clearAuthResponse.error === "cancelled") {
                void this.analyticsService.trackHeapEvent({
                    event: LoginAnalyticsEvent.LOGIN_EXIT,
                    user_id: (_a = getAnonymousUserId()) !== null && _a !== void 0 ? _a : undefined,
                    properties: { ...clearAuthData === null || clearAuthData === void 0 ? void 0 : clearAuthData.heapGlobalEventProperties },
                });
                this.state = ClearAuthState.EXITED;
            }
            else {
                this.handleAuthError();
            }
            this.stripCallbackUrlParams();
            return;
        }
        if (!clearAuthData) {
            this.handleAuthError();
            return;
        }
        const { state, organizationId, authTarget, sandboxMode, heapGlobalEventProperties, source, collectedEmail } = clearAuthData;
        if (state !== clearAuthResponse.state) {
            this.handleAuthError();
            return;
        }
        try {
            let tokenLinkedDuringAuth;
            if (source === LoginSource.ACCOUNT_LINKING) {
                tokenLinkedDuringAuth = (_b = this.persistence.getAccountLinkingData()) === null || _b === void 0 ? void 0 : _b.linkingToken;
            }
            const result = await this.authManager.authenticateByClearIAL2(clearAuthResponse.code, this.getRedirectUri(), organizationId, authTarget !== null && authTarget !== void 0 ? authTarget : AuthTarget.MedAllies, sandboxMode !== null && sandboxMode !== void 0 ? sandboxMode : false, heapGlobalEventProperties, tokenLinkedDuringAuth, (collectedEmail === null || collectedEmail === void 0 ? void 0 : collectedEmail.trim()) || undefined);
            if (isOk(result)) {
                this.handleAuthSuccess();
                if (!tokenLinkedDuringAuth) {
                    await this.authManager.redeemPendingHostedSsoLink();
                }
                this.persistence.setAccountLinkingData(null);
                void this.analyticsService.addUserProperties((_c = this.authManager.id) !== null && _c !== void 0 ? _c : undefined, {
                    loginType: "CLEAR",
                    loginCategory: "IAL2",
                });
            }
            else {
                this.handleAuthError();
            }
        }
        catch (error) {
            console.error("Error authenticating with CLEAR");
            this.handleAuthError();
        }
        this.stripCallbackUrlParams();
    }
    getRedirectUri() {
        return `${window.location.origin}`;
    }
    stripCallbackUrlParams() {
        const cleanHash = window.location.hash.split("?")[0];
        window.history.replaceState(null, "", `${window.location.origin}${window.location.pathname}${cleanHash}`);
    }
    handleAuthError() {
        this.state = ClearAuthState.ERROR;
    }
    handleAuthSuccess() {
        this.state = ClearAuthState.SUCCESS;
    }
    resetAuthStateIfNeeded() {
        const clearAuthData = this.getClearAuthData();
        if (clearAuthData) {
            if (clearAuthData.initialScreen === ClearInitialScreen.PATIENT_CONSENT_CLEAR_UPGRADE) {
                this.state = ClearAuthState.INACTIVE;
            }
            else {
                this.resetAuth();
            }
        }
    }
}
__decorate([
    observable
], ClearAuthManager.prototype, "state", void 0);
__decorate([
    computed
], ClearAuthManager.prototype, "hasCompletedAuth", null);
